import { Box, ButtonBase, makeStyles, Typography } from "@material-ui/core";
import React from "react";

import { CustomButton } from "@/components/Button";
import { Chevron } from "@/components/Chevron";
import { homeCardStyle } from "@/components/home/HomeSection";
import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import { BaseTemplate } from "@/layouts/BaseTemplate";
import theme from "@/theme";
import { NavigationPath } from "@/utilities/enums";

export const useDocumentStyles = makeStyles({
  "@global": { "body[data-property-document-confirm]": { overflow: "hidden !important" } },
  column: {
    maxWidth: 560,
    margin: "0 auto",
    minWidth: 0,
    width: "100%",
    [theme.breakpoints.up("md")]: { padding: `${layout.space[32]}px 0 ${layout.space[64]}px` },
  },
  pcHead: {
    display: "none",
    [theme.breakpoints.up("md")]: { display: "flex", flexDirection: "column", gap: layout.space[16] },
  },
  back: {
    ...textStyles["PC/Button/M"],
    color: colors["text/link"],
    minHeight: layout.space[40],
    alignSelf: "flex-start",
    gap: layout.space[4],
    padding: layout.space[12],
    margin: -layout.space[12],
  },
  pageTitle: { ...textStyles["PC/Heading/H2"], color: colors["text/heading"] },
  body: {
    padding: `${layout.space[24]}px ${layout.space[24]}px ${layout.space[32]}px`,
    display: "flex",
    flexDirection: "column",
    gap: layout.space[24],
    minWidth: 0,
  },
  grouped: { gap: layout.space[32] },
  heading: { ...textStyles["SP/Heading/H2"], [theme.breakpoints.up("md")]: textStyles["PC/Heading/H3"] },
  small: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    overflowWrap: "anywhere",
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Small"],
  },
  text: { ...textStyles["SP/Body/Large"], [theme.breakpoints.up("md")]: textStyles["PC/Body/Large"] },
  rowTitle: {
    ...textStyles["SP/Button/M"],
    [theme.breakpoints.up("md")]: textStyles["PC/Button/M"],
    overflowWrap: "anywhere",
    minWidth: 0,
    flex: 1,
  },
  rows: { display: "flex", flexDirection: "column", gap: layout.space[8], minWidth: 0 },
  section: { display: "flex", flexDirection: "column", gap: layout.space[16], minWidth: 0 },
  sectionTitle: { ...textStyles["SP/Heading/H4"], [theme.breakpoints.up("md")]: textStyles["PC/Heading/H4"] },
  row: {
    ...homeCardStyle,
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: layout.space[16],
    gap: layout.space[16],
    textAlign: "left",
  },
  fileBody: { display: "flex", flexDirection: "column", gap: layout.space[4], minWidth: 0, flex: 1 },
  empty: {
    backgroundColor: colors["surface/card"],
    borderRadius: layout.radius.lg,
    padding: layout.space[24],
    display: "flex",
    flexDirection: "column",
    gap: layout.space[8],
  },
  actionButton: { [theme.breakpoints.up("md")]: { "&&&": { width: "100%", maxWidth: 360 } } },
  menuRow: {
    ...textStyles["SP/Button/M"],
    width: "100%",
    minHeight: 52,
    justifyContent: "flex-start",
    padding: `${layout.space[16]}px ${layout.space[24]}px`,
    borderBottom: `1px solid ${colors["border/default"]}`,
    textAlign: "left",
    [theme.breakpoints.up("md")]: textStyles["PC/Button/M"],
  },
  destructive: { color: colors["status/error"] },
  cancel: { justifyContent: "center", borderBottom: "none", color: colors["text/secondary"] },
  memo: {
    [theme.breakpoints.down("sm")]: { backgroundColor: colors["surface/card"], minHeight: "calc(100vh - 56px)" },
    [theme.breakpoints.up("md")]: {
      backgroundColor: colors["surface/card"],
      borderRadius: layout.radius.lg,
      padding: `${layout.space[48]}px ${layout.space[40]}px`,
      marginTop: layout.space[16],
    },
  },
  target: {
    backgroundColor: colors["surface/base"],
    padding: layout.space[16],
    borderRadius: layout.radius.md,
    display: "flex",
    flexDirection: "column",
    gap: layout.space[4],
    minWidth: 0,
  },
  textarea: {
    width: "100%",
    minHeight: 140,
    resize: "vertical",
    padding: layout.space[16],
    border: `1px solid ${colors["border/default"]}`,
    borderRadius: layout.radius.md,
    ...textStyles["SP/Body/Large"],
    color: colors["text/primary"],
    backgroundColor: colors["surface/card"],
    fontFamily: "inherit",
    "&:focus": { outline: `2px solid ${colors["action/primary"]}`, outlineOffset: 2 },
  },
  error: { color: colors["status/error"], ...textStyles["SP/Body/Small"] },
  pagination: {
    "& .MuiToolbar-root": { padding: 0, flexWrap: "wrap", justifyContent: "flex-end" },
    "& .MuiTablePagination-spacer": { display: "none" },
    "& .MuiTablePagination-actions": { marginLeft: layout.space[8] },
  },
});

export const DocumentLayout: React.FC<{
  title: string;
  backLabel: string;
  onBack: () => void;
  navigationPath: NavigationPath;
  memo?: boolean;
}> = ({ title, backLabel, onBack, navigationPath, memo, children }) => {
  const { isMobile } = useDevice();
  const classes = useDocumentStyles();
  return (
    <BaseTemplate
      headerTitle={isMobile ? title : undefined}
      onBack={isMobile ? onBack : undefined}
      navigationPath={isMobile ? undefined : navigationPath}
      isDense
      isPcV2
      disableBottomPadding
      isBackGroundWhite={isMobile && memo}
    >
      <Box className={classes.column}>
        <Box className={classes.pcHead}>
          <ButtonBase className={classes.back} onClick={onBack}>
            <Chevron direction="back" />
            {backLabel}
          </ButtonBase>
          <Typography component="h1" className={classes.pageTitle}>
            {title}
          </Typography>
        </Box>
        {children}
      </Box>
    </BaseTemplate>
  );
};

export const DocumentLoadError: React.FC<{ onRetry: () => void }> = ({ onRetry }) => {
  const classes = useDocumentStyles();
  return (
    <Box role="alert" className={classes.empty}>
      <Typography className={classes.text}>書類の読み込みに失敗しました。もう一度お試しください。</Typography>
      <CustomButton tone="outline" buttonSize="M" onClick={onRetry}>
        再試行
      </CustomButton>
    </Box>
  );
};

export const DocumentMissingProperty: React.FC<{ title: string; onSelect: () => void }> = ({ title, onSelect }) => {
  const classes = useDocumentStyles();
  return (
    <DocumentLayout
      title={title}
      backLabel="物件一覧"
      onBack={onSelect}
      navigationPath={NavigationPath.PROPERTY_MANAGEMENT_OWNED}
    >
      <Box className={classes.body}>
        <Box className={classes.empty}>
          <Typography component="h2" className={classes.heading}>
            物件を選択してください
          </Typography>
          <Typography className={classes.small}>書類を確認する物件を選択してください。</Typography>
        </Box>
        <CustomButton className={classes.actionButton} buttonSize="M" onClick={onSelect}>
          物件一覧へ
        </CustomButton>
      </Box>
    </DocumentLayout>
  );
};
